import { presentationSearchResults$ } from '../../report/ReportReader/presentationSearchResults$';
import { combineLatest } from 'rxjs';
import { reportReader$ } from '../../report/ReportReader/reportReader$';
import { map } from 'rxjs/operators';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { getSearchResultsForReportSlide } from './utils';
import {
  getAddFilterButtonOptions,
  getFiltersToRender,
  ReportError,
  ReportEventLocations,
} from '@ardoq/report-reader';
import { getReportReaderCommands } from '../../report/ReportReader/commands/reportReaderCommands';
import { ReportSlideProps } from './types';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from '../../streams/i18nLocale$';
import { presentationAggregatesResults$ } from '../../report/ReportReader/aggregatesResults$';
import { track, noTracking } from 'tracking/track';
import { getAggregatesResultsState } from '../../report/ReportReader/utils';
import { api } from '@ardoq/api';
import { getSolutionMetadata } from '@ardoq/core';
import { isPptxExportRunning$ } from 'presentation/pptxExport/pptxExport$';
import { toReportSlideCapture } from './reportSlideCapture';
import { isReportCaptureBusy } from 'report/ReportReader/capturePagination';

const reportSlideViewModel$ = combineLatest([
  reportReader$,
  presentationSearchResults$,
  presentationAggregatesResults$,
  currentUserPermissionContext$,
  locale$,
  i18nLocale$,
  isPptxExportRunning$,
]).pipe(
  map(
    ([
      {
        sort,
        page,
        pagination,
        filters,
        reportId,
        isSearchBeingExecuted,
        isAggregatesBeingExecuted,
        presentationId,
        slideId,
        isFilteringDisabled,
        paginationBeforeCapture,
        reportAPIError,
        hasFinishedFetchReport,
        report,
      },
      {
        apiError: searchResultsAPIError,
        searchResults: apiSearchResults,
        filterableColumns,
        slideId: searchResultsSlideId,
      },
      aggregatesResultsState,
      permissionContext,
      currentUserLocale,
      i18nLocale,
      isExporting,
    ]): ReportSlideProps => {
      const isPublicPresentation = !permissionContext?.user?._id;
      const searchResults = getSearchResultsForReportSlide(
        apiSearchResults,
        isPublicPresentation
      );
      const { aggregateResults, aggregateError } = getAggregatesResultsState({
        aggregatesResultsState,
        totalNumberOfRows: searchResults?.totalNumberOfRows ?? 0,
        selectedAggregate: searchResults?.selectedAggregate ?? null,
      });
      const commands = getReportReaderCommands({
        filters,
        reportId: reportId!,
        searchResults,
        presentationId: presentationId ?? undefined,
        slideId,
        eventLocation: ReportEventLocations.PRESENTATION,
      });

      const error = !hasFinishedFetchReport
        ? null
        : api.isForbidden(reportAPIError)
          ? ReportError.USER_IS_MISSING_READ_ACCESS_TO_REPORT
          : reportAPIError || searchResultsAPIError;

      const isLoading = !error && !searchResults;

      return {
        capture: toReportSlideCapture({
          readerSlideId: slideId,
          searchResultsSlideId,
          hasReportError: Boolean(reportAPIError),
          hasError: Boolean(error),
          isBusy: isReportCaptureBusy({
            paginationBeforeCapture,
            isLoading,
            isSearchBeingExecuted,
            isAggregatesBeingExecuted,
            searchResults: apiSearchResults,
          }),
        }),
        isCaptureMode: isExporting,
        filterableColumns,
        searchResults,
        commands,
        addFilterButtonOptions: getAddFilterButtonOptions({
          filterableColumns,
          filters,
          dataSource: searchResults?.datasource,
          removeFilterExpression: commands.removeFilterExpression,
          addEmptyFilterExpression: commands.addEmptyFilterExpression,
        }),
        filtersToRender: getFiltersToRender({
          filters,
          filterableColumns,
          locale: currentUserLocale,
          i18nLocale,
          removeFilterExpression: commands.removeFilterExpression,
          setFilterExpression: commands.setFilterExpression,
          loadComponentSuggestions: commands.loadComponentSuggestions,
        }),
        sortFilterPagination: { sort, page, pagination, filters },
        reportId: reportId!,
        solutionMetadata: report ? getSolutionMetadata(report) : undefined,
        slideId,
        presentationId,
        isSearchBeingExecuted,
        permissionContext,
        currentUserLocale,
        aggregatesResultsState: { aggregateResults, aggregateError },
        isAggregatesBeingExecuted,
        isFilteringDisabled,
        error,
        isLoading,
        trackEvent: isPublicPresentation ? noTracking : track,
        i18nLocale,
      };
    }
  )
);

export { reportSlideViewModel$ };
